import { styled } from '@mui/material';
import React from 'react';
import { VariantComponentProps } from './variant';
import Adornment from './Adornment';
import { Input } from './Input';

const Base = styled('div', {
    shouldForwardProp: prop =>
        prop !== 'disabled' &&
        prop !== 'error' &&
        prop !== 'focused' &&
        prop !== 'height' &&
        prop !== 'backgroundColor' &&
        prop !== 'sx',
})<{
    disabled?: boolean;
    error?: boolean;
    focused: boolean;
    height: number;
    backgroundColor?: string;
}>(({ theme, disabled, error, focused, height, backgroundColor }) => ({
    'position': 'relative',
    'width': '100%',
    'display': 'flex',
    'justifyContent': 'flex-start',
    'alignItems': 'center',
    'borderRadius': theme.shape.borderRadius,
    'borderWidth': '0px',
    'borderColor': `rgba(0, 0, 0, 0.23)`,
    'backgroundColor': backgroundColor || theme.palette.background.paper,
    'overflow': 'hidden',
    'height': height,
    'cursor': 'text',

    ...(focused && {
        borderColor: theme.palette.primary.main,
    }),

    ...(disabled && {
        backgroundColor: backgroundColor || theme.palette.grey[50],
        cursor: 'auto !important',
    }),

    ...(error && {
        borderColor: theme.palette.error.main,
    }),

    '& > input:-webkit-autofill': {
        WebkitBoxShadow: `0 0 0 1000px ${backgroundColor || theme.palette.background.paper} inset`,
    },
    '& > input:-webkit-autofill:hover': {
        WebkitBoxShadow: `0 0 0 1000px ${backgroundColor || theme.palette.background.paper} inset`,
    },
    '& > input:-webkit-autofill:focus': {
        WebkitBoxShadow: `0 0 0 1000px ${backgroundColor || theme.palette.background.paper} inset`,
    },
    '& > input:-webkit-autofill:active': {
        WebkitBoxShadow: `0 0 0 1000px ${backgroundColor || theme.palette.background.paper} inset`,
    },
}));

// Provides the border. Having the border separate from the base makes it easier to handle the border width change on focus.
// It inherits the styling of the border from the base so it can be easily customized.
const Border = styled('div', {
    shouldForwardProp: prop => prop !== 'focused' && prop !== 'sx',
})<{
    focused: boolean;
}>(({ focused }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: 'inherit',
    borderColor: 'inherit',
    borderWidth: '1px',
    borderStyle: 'solid',
    pointerEvents: 'none',
    ...(focused && {
        borderWidth: '2px',
    }),
}));

const StandardInput = React.forwardRef<HTMLInputElement, VariantComponentProps>(
    (
        {
            // Core behavior props
            height = 40,
            error,
            disabled,

            // Styling props
            backgroundColor,
            sx,
            inputSx,

            // UI elements inside the input
            startAdornment,
            startAdornmentRef,
            startAdornmentWidth,
            startAdornmentSx,
            endAdornment,
            endAdornmentRef,
            endAdornmentWidth,
            endAdornmentSx,

            // Event handlers for <Base>
            onBaseClick,
            onBaseMouseEnter,
            onBaseMouseLeave,

            // Event handlers
            onFocus,
            onBlur,

            // Other input-related props
            ...props
        },
        externalRef
    ) => {
        const [focused, setFocused] = React.useState(false);
        const inputRef = React.useRef<HTMLInputElement | null>(null);

        // If externalRef is provided, pass it down to allow forwarding
        React.useImperativeHandle(externalRef, () => inputRef.current!);

        const handleOnFocus = (e: React.FocusEvent<HTMLInputElement>) => {
            setFocused(true);
            if (onFocus) {
                onFocus(e);
            }
        };

        const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
            setFocused(false);
            if (onBlur) {
                onBlur(e);
            }
        };

        // Handle clicking on <Base> to focus the input
        const handleBaseClick = (e: React.MouseEvent<HTMLDivElement>) => {
            inputRef.current?.focus(); // Manually focus input
            onBaseClick?.(e); // Call user-defined handler if provided
        };

        return (
            <Base
                disabled={disabled}
                error={error}
                focused={focused}
                height={height}
                backgroundColor={backgroundColor}
                sx={sx}
                onClick={handleBaseClick}
                onMouseEnter={onBaseMouseEnter}
                onMouseLeave={onBaseMouseLeave}>
                <Input
                    {...props}
                    ref={inputRef}
                    disabled={disabled}
                    onFocus={handleOnFocus}
                    onBlur={handleOnBlur}
                    startAdornmentWidth={startAdornmentWidth}
                    endAdornmentWidth={endAdornmentWidth}
                    sx={inputSx}
                />
                <Border focused={focused} />
                {startAdornment && (
                    <Adornment
                        ref={startAdornmentRef}
                        position='start'
                        sx={startAdornmentSx}>
                        {startAdornment}
                    </Adornment>
                )}
                {endAdornment && (
                    <Adornment
                        ref={endAdornmentRef}
                        position='end'
                        sx={endAdornmentSx}>
                        {endAdornment}
                    </Adornment>
                )}
            </Base>
        );
    }
);
StandardInput.displayName = 'StandardInput';

export default StandardInput;
